import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";

import { DocumentosDelRelleno } from "@/components/ia/DocumentosDelRelleno";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { usePreciosDeIa } from "@/hooks/useConsumoDeIa";
import {
  useGuardarContextoDeCampo,
  useGuardarIaFuncion,
  useIaCampos,
  useIaFunciones,
  useOlvidarContextoDeCampo,
} from "@/hooks/useMantenedorDeIa";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { modelosOfrecidos, type IaCampo, type IaFuncion } from "@/lib/mantenedorDeIa";

/* ---------------------------------------------------------------------------
   Mantenedor de IA (`/admin/ia`).

   Lo que esta pantalla existe para permitir: ajustar el prompt y el modelo de
   una función SIN desplegar. El prompt es lo que más se va a iterar de todo el
   módulo, y quien lo va a iterar no es quien despliega.

   POR QUÉ NO COPIA EL MODAL DEL PROTOTIPO. El "Configurar prompt" del
   prototipo (37 SA) trae un panel de "Fuentes disponibles" con casillas y
   fichas de "Fuente citada": está pensado para prompts que citan documentos.
   Los nuestros no consultan nada, así que ese panel sería media pantalla
   apagada. Lo que sí agregamos y aquel no tiene: temperatura, opciones del
   proveedor y el uso real de cada función.

   Maestro-detalle y no una tabla con diálogo: lo que se edita acá es texto
   largo, y un prompt no se lee en una celda.

   EL DETALLE VA EN TRES SECCIONES Y EN ESTE ORDEN, y no es cosmética: es la
   corrección de una pantalla que con dos funciones se leía bien y con nueve
   dejó de servir.

   1. **Lo que se toca rápido va ARRIBA.** Cambiar el modelo o la temperatura
      es un ajuste de un segundo; redactar el prompt es una sesión. Con el
      prompt primero, el ajuste corto quedaba a dos pantallas de scroll del
      título — y el botón de guardar, más abajo todavía.
   2. **El prompt tiene ALTO PROPIO y scrollea adentro.** El `Textarea` del DS
      trae `field-sizing-content`, así que crece con su contenido: los prompts
      de hoy miden entre 85 y 119 líneas y la caja se dibujaba de más de dos
      mil píxeles, empujando todo lo demás fuera de la vista. `rows` no lo
      corrige — `field-sizing` gana. Se fija con `field-sizing-fixed` y se deja
      `resize-y` para quien quiera ver el prompt entero de una vez.
   3. **La barra de acción es PEGAJOSA.** Con el detalle más alto que la
      pantalla, un botón de guardar al pie obliga a bajar para confirmar un
      cambio que se hizo arriba. Funciona porque `main` es quien scrollea (ver
      el comentario de `MainLayout`).
--------------------------------------------------------------------------- */

/** Solo los que tienen conexión cableada y precios. Los demás se ofrecen
    cuando exista la suya (y en el API, en `IaFuncion::PROVEEDORES_HABILITADOS`,
    que tiene que coincidir con esta lista). `gemini_global` es Vertex en la
    ubicación `global`, donde responde Gemini 4 Argon. */
const PROVEEDORES = [
  { valor: "gemini", etiqueta: "Gemini (Google)" },
  { valor: "gemini_global", etiqueta: "Gemini global (Vertex)" },
  { valor: "openai", etiqueta: "OpenAI" },
  { valor: "anthropic", etiqueta: "Anthropic (Claude)" },
] as const;

const ETIQUETA_CATEGORIA = {
  asistencia: "Asisten al usuario",
  generacion: "Generan contenido",
} as const;

export function MantenedorDeIa() {
  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      {/* Dos cosas distintas que se ajustan sin desplegar: lo que dice y usa
          cada función (prompt, modelo) y de qué documentos lee Rellenar con IA.
          Pestañas y no una sola pantalla larga: la primera ya es un
          maestro-detalle que ocupa todo el alto. */}
      <Tabs variant="underline" defaultValue="funciones" className="gap-5">
        <TabsList>
          <TabsTrigger value="funciones">Funciones</TabsTrigger>
          <TabsTrigger value="documentos">Documentos de Rellenar con IA</TabsTrigger>
        </TabsList>
        <TabsContent value="funciones">
          <FuncionesDeIa />
        </TabsContent>
        <TabsContent value="documentos">
          <DocumentosDelRelleno />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FuncionesDeIa() {
  const funciones = useIaFunciones();
  const campos = useIaCampos();
  const [seleccionadaId, setSeleccionadaId] = useState<number | null>(null);

  /* La primera función queda elegida sola: un detalle vacío al abrir obligaría
     a un clic que no decide nada. Solo actúa mientras no haya selección, para
     no pisar la del usuario cuando la lista se refresca tras guardar. */
  useEffect(() => {
    if (seleccionadaId === null && funciones.data?.length) {
      setSeleccionadaId(funciones.data[0]!.id);
    }
  }, [funciones.data, seleccionadaId]);

  const grupos = useMemo(() => {
    const porCategoria = new Map<string, IaFuncion[]>();
    for (const funcion of funciones.data ?? []) {
      porCategoria.set(funcion.categoria, [
        ...(porCategoria.get(funcion.categoria) ?? []),
        funcion,
      ]);
    }
    return porCategoria;
  }, [funciones.data]);

  if (funciones.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (funciones.isError) {
    return (
      <div>
        <p className="text-destructive text-sm">
          No pudimos cargar las funciones de IA. Recarga la página para volver a intentarlo.
        </p>
      </div>
    );
  }

  const seleccionada = funciones.data?.find((f) => f.id === seleccionadaId) ?? null;

  return (
    <div className="space-y-5">
      {/* Acceso directo al gasto: cuánto se gastó y en qué es una pregunta
          distinta de "cómo se configura cada función", y vive en su propia
          pantalla (`odd/tasks/consumo-de-ia.md`) — acá solo el enlace. */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-ink-muted max-w-[70ch] text-sm">
          Cada lugar del producto donde se ejecuta IA. Aquí se ajusta su prompt y el modelo que
          usa, sin necesidad de un despliegue.
        </p>
        <Button asChild variant="outline">
          <Link to="/admin/ia/consumo">Consumo y costos</Link>
        </Button>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[17.5rem_1fr]">
        <nav className="space-y-5" aria-label="Funciones de IA">
          {[...grupos.entries()].map(([categoria, delGrupo]) => (
            <div key={categoria} className="space-y-1.5">
              <p className="text-ink-hint px-1 text-xs font-bold tracking-wide uppercase">
                {ETIQUETA_CATEGORIA[categoria as keyof typeof ETIQUETA_CATEGORIA] ?? categoria}
              </p>
              {delGrupo.map((funcion) => (
                <FilaDeFuncion
                  key={funcion.id}
                  funcion={funcion}
                  activa={funcion.id === seleccionadaId}
                  onSeleccionar={() => setSeleccionadaId(funcion.id)}
                />
              ))}
            </div>
          ))}
        </nav>

        {seleccionada ? (
          <DetalleDeFuncion
            // La `key` reinicia el formulario al cambiar de función: sin ella,
            // los campos conservarían el texto de la anterior y se podría
            // guardar el prompt de una función sobre otra.
            key={seleccionada.id}
            funcion={seleccionada}
            campos={campos.data ?? []}
          />
        ) : (
          <Empty className="rounded-xl border">
            <EmptyHeader>
              <EmptyTitle>Todavía no hay funciones de IA</EmptyTitle>
              <EmptyDescription>
                Aparecen aquí a medida que se construye cada pantalla que las usa.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
    </div>
  );
}

/**
 * Una función en la lista de la izquierda.
 *
 * DOS LÍNEAS Y NO TRES: el uso solo se imprime cuando la función se usó de
 * verdad. Con nueve funciones, ocho renglones diciendo "Sin uso todavía"
 * alargan la lista hasta obligar a scrollearla para elegir, y no distinguen
 * nada — lo que sí distingue es cuál se está usando.
 */
function FilaDeFuncion({
  funcion,
  activa,
  onSeleccionar,
}: {
  funcion: IaFuncion;
  activa: boolean;
  onSeleccionar: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSeleccionar}
      aria-current={activa ? "true" : undefined}
      className={`w-full rounded-lg border px-3.5 py-2.5 text-left transition-colors ${
        activa ? "bg-card border-primary shadow-sm" : "border-transparent hover:bg-card"
      }`}
    >
      <span className="block text-sm font-bold">{funcion.nombre}</span>
      {/* Metadata en mono y chica: acompaña, no compite con el nombre. */}
      <span className="text-ink-hint mt-0.5 block truncate font-mono text-xs">
        {funcion.modelo}
      </span>
      {funcion.uso && funcion.uso.corridas > 0 ? (
        <span className="text-ink-hint mt-1 block text-xs">
          {funcion.uso.corridas} corridas · {funcion.uso.aceptadas} aceptadas
        </span>
      ) : null}
    </button>
  );
}

/** Una sección del detalle: título, ayuda y sus controles. */
function Seccion({
  titulo,
  ayuda,
  children,
}: {
  titulo: string;
  ayuda?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-card space-y-4 rounded-xl border p-6">
      <div>
        <h3 className="text-h3 text-ink">{titulo}</h3>
        {ayuda ? <p className="text-ink-muted mt-1 max-w-[70ch] text-sm">{ayuda}</p> : null}
      </div>
      {children}
    </section>
  );
}

function DetalleDeFuncion({ funcion, campos }: { funcion: IaFuncion; campos: IaCampo[] }) {
  const guardar = useGuardarIaFuncion();

  /* Lo que el servidor tiene hoy, en la forma en que lo edita el formulario.
     Sirve para dos cosas: sembrar el estado y saber si hay cambios sin
     guardar, que es la comparación de más abajo. */
  const temperaturaGuardada = funcion.temperatura === null ? "" : String(funcion.temperatura);
  const opcionesGuardadas = funcion.opciones_proveedor
    ? JSON.stringify(funcion.opciones_proveedor, null, 2)
    : "";

  const [instruccion, setInstruccion] = useState(funcion.instruccion);
  /* El texto que quedó guardado como BORRADOR (un prompt que redacta secciones no
     entra en uso al guardarlo: se publica desde el Laboratorio de IA). Mientras
     lo escrito coincida con él, no hay nada sin guardar. */
  const [guardadoComoBorrador, setGuardadoComoBorrador] = useState<string | null>(null);
  const [proveedor, setProveedor] = useState(funcion.proveedor);
  const [modelo, setModelo] = useState(funcion.modelo);
  const [temperatura, setTemperatura] = useState(temperaturaGuardada);

  /* Cambiar de proveedor también cambia de catálogo: el modelo de uno no existe
     en el otro y el servidor lo rechazaría. Se vuelve al guardado si el
     proveedor es el original, y si no al primero que ofrece el nuevo. */
  const cambiarProveedor = (nuevo: string) => {
    setProveedor(nuevo);
    setModelo(
      nuevo === funcion.proveedor
        ? funcion.modelo
        : (modelosOfrecidos(precios, nuevo, null)[0] ?? ""),
    );
  };

  /* Si el catálogo falla o aún no llega, la lista queda con el modelo guardado
     y la pantalla sigue siendo usable. */
  const { data: precios } = usePreciosDeIa();
  const modelosDisponibles = modelosOfrecidos(
    precios,
    proveedor,
    proveedor === funcion.proveedor ? funcion.modelo : null,
  );
  const sinModelosEnElCatalogo =
    precios !== undefined && !precios.some((p) => p.proveedor === proveedor);

  const [opciones, setOpciones] = useState(opcionesGuardadas);
  const [errores, setErrores] = useState<Record<string, string>>({});
  /* Las opciones del proveedor se tocan una vez por función y son ruido el
     resto del tiempo, así que arrancan plegadas — salvo que ya tengan algo
     escrito, donde esconderlas ocultaría configuración vigente. */
  const [opcionesAbiertas, setOpcionesAbiertas] = useState(Boolean(funcion.opciones_proveedor));

  /* Con el botón de guardar pegado arriba y el prompt abajo, el cambio y su
     confirmación dejan de verse juntos: sin este aviso, salir de la función
     —un clic en la lista de la izquierda— se lleva el trabajo en silencio,
     porque la `key` del detalle reinicia el formulario. */
  const hayCambios =
    instruccion !== (guardadoComoBorrador ?? funcion.instruccion) ||
    proveedor !== funcion.proveedor ||
    modelo !== funcion.modelo ||
    temperatura !== temperaturaGuardada ||
    opciones !== opcionesGuardadas;

  function enviar() {
    setErrores({});

    /* Las opciones del proveedor se escriben a mano porque su forma cambia con
       él: no hay un formulario posible que sirva para todos. Lo que sí se
       puede es no dejar guardar algo que no es JSON — si se guardara, la
       función fallaría recién al ejecutarse, con el usuario esperando. */
    let opcionesParseadas: Record<string, unknown> | null = null;
    if (opciones.trim()) {
      try {
        opcionesParseadas = JSON.parse(opciones) as Record<string, unknown>;
      } catch {
        // Se despliega el panel: un error sobre un campo plegado es un botón
        // que no responde y ninguna explicación a la vista.
        setOpcionesAbiertas(true);
        setErrores({ opciones_proveedor: "Eso no es un JSON válido." });
        return;
      }
    }

    guardar.mutate(
      {
        id: funcion.id,
        instruccion,
        proveedor,
        modelo,
        temperatura: temperatura.trim() === "" ? null : Number(temperatura.replace(",", ".")),
        opciones_proveedor: opcionesParseadas,
      },
      {
        onSuccess: (guardada) => {
          /* El formulario se resiembra con lo que quedó guardado, no con lo
             que se mandó: si el servidor normaliza algo —una temperatura, un
             prompt— el aviso de "Sin guardar" se quedaría encendido sobre una
             diferencia que ya no existe, y el detalle no se remonta (su `key`
             es el id, que no cambió). */
          if (guardada.borrador_guardado) {
            setGuardadoComoBorrador(instruccion);
          } else {
            setGuardadoComoBorrador(null);
            setInstruccion(guardada.instruccion);
          }
          setProveedor(guardada.proveedor);
          setModelo(guardada.modelo);
          setTemperatura(guardada.temperatura === null ? "" : String(guardada.temperatura));
          setOpciones(
            guardada.opciones_proveedor
              ? JSON.stringify(guardada.opciones_proveedor, null, 2)
              : "",
          );
          toast.success(
            guardada.borrador_guardado ? "Prompt guardado como borrador." : "Configuración guardada.",
          );
        },
        onError: (error) => {
          const delServidor = parseValidationErrors(error);
          if (delServidor) {
            if (delServidor.opciones_proveedor) setOpcionesAbiertas(true);
            setErrores(delServidor);
            toast.error("Revisa los campos marcados.");
            return;
          }
          toast.error(getApiErrorMessage(error, "No pudimos guardar la configuración."));
        },
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* `bg-background` y no transparente: pegada, se dibuja encima de lo que
          scrollea por debajo. */}
      <div className="bg-background sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 pt-1 pb-3">
        <div className="min-w-0">
          <h2 className="text-h3 text-ink truncate">{funcion.nombre}</h2>
          <p className="text-ink-hint mt-0.5 text-xs">
            {/* El nulo de `pantalla` significa algo y se dice, no se esconde. */}
            {funcion.pantalla ?? "Disponible en todos los campos de texto con asistencia"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {hayCambios ? <Badge variant="warning">Sin guardar</Badge> : null}
          <Button type="button" onClick={enviar} disabled={guardar.isPending}>
            {guardar.isPending ? <Spinner /> : null}
            Guardar configuración
          </Button>
        </div>
      </div>

      {guardadoComoBorrador !== null && instruccion === guardadoComoBorrador ? (
        <p role="status" className="bg-info-soft text-info-soft-foreground rounded-xl px-3.5 py-2.5 text-sm font-bold">
          Guardado como borrador. Publícalo desde el{" "}
          <Link to="/admin/ia/laboratorio#prompts" className="underline">
            Laboratorio de IA
          </Link>
          .
        </p>
      ) : null}

      {funcion.descripcion || funcion.uso ? (
        <div className="space-y-2 px-1">
          {funcion.descripcion ? (
            <p className="text-ink-muted max-w-[70ch] text-sm">{funcion.descripcion}</p>
          ) : null}
          {funcion.uso ? <ResumenDeUso funcion={funcion} /> : null}
        </div>
      ) : null}

      <Seccion
        titulo="Modelo"
        ayuda="Con qué se ejecuta esta función. Se cambia sin desplegar y rige desde la próxima corrida."
      >
        <div className="grid gap-5 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="proveedor">Proveedor</Label>
            <Select value={proveedor} onValueChange={cambiarProveedor}>
              <SelectTrigger id="proveedor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVEEDORES.map((p) => (
                  <SelectItem key={p.valor} value={p.valor}>
                    {p.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errores.proveedor ? (
              <p className="text-destructive text-xs font-medium">{errores.proveedor}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="modelo">Modelo</Label>
            {/* La lista sale del catálogo de precios: se edita sin desplegar y
                un modelo sin precio no se puede costear. */}
            <Select value={modelo} onValueChange={setModelo}>
              <SelectTrigger id="modelo" aria-invalid={Boolean(errores.modelo)}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {modelosDisponibles.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {sinModelosEnElCatalogo ? (
              <p className="text-muted-foreground text-xs">
                No hay modelos con precio para este proveedor. Cárgalos en{" "}
                <Link to="/admin/ia/consumo" className="underline">
                  Consumo y costos
                </Link>
                .
              </p>
            ) : null}
            {errores.modelo ? (
              <p className="text-destructive text-xs font-medium">{errores.modelo}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="temperatura">Temperatura</Label>
            <Input
              id="temperatura"
              inputMode="decimal"
              placeholder="Ej: 0,2"
              value={temperatura}
              onChange={(evento) => setTemperatura(evento.target.value)}
              aria-invalid={Boolean(errores.temperatura)}
            />
            <p className="text-ink-hint text-xs">
              Más baja, más fiel al original. Vacío usa la del modelo.
            </p>
            {errores.temperatura ? (
              <p className="text-destructive text-xs font-medium">{errores.temperatura}</p>
            ) : null}
          </div>
        </div>

        <Collapsible
          open={opcionesAbiertas}
          onOpenChange={setOpcionesAbiertas}
          className="border-t pt-4"
        >
          <CollapsibleTrigger className="text-ink-body hover:text-ink group flex items-center gap-2 text-sm font-bold transition-colors">
            <ChevronDown
              className="size-4 transition-transform group-data-[state=open]:rotate-180"
              aria-hidden="true"
            />
            Opciones del proveedor
            {funcion.opciones_proveedor ? (
              <Badge variant="info">Configuradas</Badge>
            ) : null}
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-3 space-y-2">
            <Label htmlFor="opciones-proveedor" className="sr-only">
              Opciones del proveedor
            </Label>
            <Textarea
              id="opciones-proveedor"
              rows={4}
              className="field-sizing-fixed h-28 resize-y font-mono text-xs"
              placeholder={'{ "gemini": { "thinkingConfig": { "thinkingBudget": 0 } } }'}
              value={opciones}
              onChange={(evento) => setOpciones(evento.target.value)}
              aria-invalid={Boolean(errores.opciones_proveedor)}
            />
            <p className="text-ink-hint max-w-[70ch] text-xs">
              Aquí va lo que cada proveedor pide a su manera, como el modo de pensamiento. Se
              escribe agrupado por proveedor porque la forma cambia con cada uno.
            </p>
            {errores.opciones_proveedor ? (
              <p className="text-destructive text-xs font-medium">
                {errores.opciones_proveedor}
              </p>
            ) : null}
          </CollapsibleContent>
        </Collapsible>
      </Seccion>

      <Seccion
        titulo="Prompt base"
        ayuda="Las instrucciones con las que corre la función. Es lo que más se itera, y cambiarlo no necesita un despliegue."
      >
        <div className="space-y-2">
          <Label htmlFor="prompt-base" className="sr-only">
            Prompt base
          </Label>
          {/* `field-sizing-fixed` PISA AL `field-sizing-content` DEL DS, y de eso
              depende toda la pantalla: sin él la caja crece con su contenido y
              un prompt de cien líneas la dibuja de más de dos mil píxeles.
              El alto sale del contenedor que scrollea —`main` declara
              `container-type: size`— así que la caja ocupa una porción estable
              de la pantalla en vez de una cifra estimada a ojo. */}
          <Textarea
            id="prompt-base"
            value={instruccion}
            onChange={(evento) => setInstruccion(evento.target.value)}
            aria-invalid={Boolean(errores.instruccion)}
            className="field-sizing-fixed h-[clamp(18rem,45cqh,45rem)] resize-y font-mono text-xs leading-relaxed"
          />
          {errores.instruccion ? (
            <p className="text-destructive text-xs font-medium">{errores.instruccion}</p>
          ) : null}
        </div>
      </Seccion>

      {/* SOLO DONDE SIRVE, y lo decide el SERVIDOR. `instruccionPara()` —el
          único lugar que suma un contexto de campo al prompt— lo llama
          `App\Ai\MejorarRedaccion` y nadie más: las otras ocho funciones leen
          `instruccion` directo. Dibujado en todas, este panel ofrecía guardar
          un texto que no se aplicaría nunca, debajo de cada prompt. */}
      {funcion.usa_contexto_por_campo ? (
        <ContextosPorCampo funcion={funcion} campos={campos} />
      ) : null}
    </div>
  );
}

/**
 * Cuánto se usó y qué se hizo con lo que propuso.
 *
 * La proporción entre aceptadas y descartadas es lo único que dice si la
 * función SIRVE. El total solo dice cuánto se usa, que no es lo mismo.
 */
function ResumenDeUso({ funcion }: { funcion: IaFuncion }) {
  const uso = funcion.uso!;

  if (uso.corridas === 0) {
    return <span className="text-ink-hint text-xs">Todavía nadie la ha usado.</span>;
  }

  return (
    <span className="text-ink-hint flex flex-wrap items-center gap-2 text-xs">
      <span className="font-mono">{uso.corridas}</span> corridas ·
      <Badge variant="success">{uso.aceptadas} aceptadas</Badge>
      <Badge variant="warning">{uso.descartadas} descartadas</Badge>·
      <span className="font-mono">
        {(uso.tokens_entrada + uso.tokens_salida).toLocaleString("es-CL")}
      </span>{" "}
      tokens
    </span>
  );
}

/**
 * Los campos que pueden tener contexto propio, con el que ya tienen.
 *
 * La lista viene del CATÁLOGO y no de un campo de texto: escribir el código a
 * mano dejaba pasar un `informe.descripion` que se guardaba sin error y no se
 * aplicaba nunca. Ofrecer la lista hace ese error imposible.
 */
function ContextosPorCampo({ funcion, campos }: { funcion: IaFuncion; campos: IaCampo[] }) {
  const guardar = useGuardarContextoDeCampo();
  const olvidar = useOlvidarContextoDeCampo();
  const [editando, setEditando] = useState<string | null>(null);
  const [texto, setTexto] = useState("");

  if (campos.length === 0) return null;

  function abrir(campo: IaCampo, actual: string) {
    setEditando(campo.codigo);
    setTexto(actual);
  }

  return (
    <Seccion
      titulo="Contexto por campo"
      ayuda="Un campo puede sumarle algo propio al prompt base. Se suma, no lo reemplaza: si corriges una regla general, sigue valiendo para todos."
    >
      <ul className="space-y-2">
        {campos.map((campo) => {
          const propio = funcion.campos.find((c) => c.campo === campo.codigo);
          const abierto = editando === campo.codigo;

          return (
            <li key={campo.id} className="rounded-xl border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-bold">
                    {/* Dot de estado: distingue configurado de vacío sin
                        obligar a abrir cada uno. */}
                    <span
                      className={`size-1.5 rounded-full ${
                        propio ? "bg-success-dot" : "bg-ink-ghost"
                      }`}
                      aria-hidden="true"
                    />
                    {campo.nombre}
                  </p>
                  <p className="text-ink-hint mt-1 text-xs">{campo.pantalla}</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => abrir(campo, propio?.instruccion ?? "")}
                  >
                    {propio ? "Editar contexto" : "Agregar contexto"}
                  </Button>
                  {propio ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={olvidar.isPending}
                      onClick={() =>
                        olvidar.mutate(
                          { funcionId: funcion.id, campo: campo.codigo },
                          {
                            onSuccess: () => {
                              setEditando(null);
                              toast.success("El campo vuelve a usar el prompt base.");
                            },
                          },
                        )
                      }
                    >
                      Quitar
                    </Button>
                  ) : null}
                </div>
              </div>

              {abierto ? (
                <div className="mt-4 space-y-2">
                  <Label htmlFor={`contexto-${campo.id}`}>Contexto de este campo</Label>
                  <Textarea
                    id={`contexto-${campo.id}`}
                    rows={4}
                    value={texto}
                    onChange={(evento) => setTexto(evento.target.value)}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={guardar.isPending}
                      onClick={() =>
                        guardar.mutate(
                          {
                            funcionId: funcion.id,
                            campo: campo.codigo,
                            instruccion: texto,
                          },
                          {
                            onSuccess: () => {
                              setEditando(null);
                              toast.success("Contexto guardado.");
                            },
                            onError: (error) =>
                              toast.error(
                                getApiErrorMessage(error, "No pudimos guardar el contexto."),
                              ),
                          },
                        )
                      }
                    >
                      {guardar.isPending ? <Spinner /> : null}
                      Guardar
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditando(null)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : propio ? (
                <p className="text-ink-body mt-3 max-w-[70ch] text-sm text-pretty">
                  {propio.instruccion}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Seccion>
  );
}
